import React, { useLayoutEffect, useRef } from '../../../../lib/teact/teact';
import { withGlobal } from '../../../../global';

import type { Account, AccountType } from '../../../../global/types';
import type { Layout } from '../../../../hooks/useMenuPosition';

import { selectAccountSettings } from '../../../../global/selectors';
import buildClassName from '../../../../util/buildClassName';
import { getCardGradient, getCardGradientStyle } from '../../../../util/cardColor';
import { formatAccountAddresses } from '../../../../util/formatAccountAddress';
import { OPEN_CONTEXT_MENU_CLASS_NAME } from './constants';

import { useDeviceScreen } from '../../../../hooks/useDeviceScreen';
import useFontScale from '../../../../hooks/useFontScale';
import useLang from '../../../../hooks/useLang';
import useLastCallback from '../../../../hooks/useLastCallback';
import useWindowSize from '../../../../hooks/useWindowSize';
import useAccountContextMenu from './hooks/useAccountContextMenu';

import DropdownMenu from '../../../ui/DropdownMenu';
import MenuBackdrop from '../../../ui/MenuBackdrop';
import SensitiveData from '../../../ui/SensitiveData';

import styles from './AccountWalletCard.module.scss';

interface OwnProps {
  isActive: boolean;
  isTestnet?: boolean;
  accountId: string;
  byChain: Account['byChain'];
  accountType: AccountType;
  title?: string;
  profile?: Account['profile'];
  balanceData?: {
    wholePart: string;
    fractionPart?: string;
    currencySymbol: string;
  };
  withContextMenu?: boolean;
  isSensitiveDataHidden?: true;
  onClick: (accountId: string) => void;
  onRename: (accountId: string) => void;
  onReorder: NoneToVoidFunction;
  onLogOut: (accountId: string) => void;
}

interface StateProps {
  accentColorIndex?: number;
}

const CONTEXT_MENU_VERTICAL_SHIFT_PX = 6;

function AccountWalletCard({
  isActive,
  isTestnet,
  accountId,
  byChain,
  accountType,
  title,
  profile,
  balanceData,
  withContextMenu,
  isSensitiveDataHidden,
  onClick,
  onRename,
  onReorder,
  onLogOut,
  accentColorIndex,
}: OwnProps & StateProps) {
  const lang = useLang();
  const balanceRef = useRef<HTMLDivElement>();
  const contentRef = useRef<HTMLDivElement>();
  const menuRef = useRef<HTMLDivElement>();
  const { isPortrait } = useDeviceScreen();
  const { width: screenWidth } = useWindowSize();
  const { updateFontScale } = useFontScale(balanceRef);

  // Screen width affects font size only in portrait orientation
  const screenWidthDep = isPortrait ? screenWidth : 0;
  const isHardware = accountType === 'hardware';
  const isViewMode = accountType === 'view';
  const isVault = profile === 'vault';
  const formattedAddress = formatAccountAddresses(byChain, 'x-small');

  const handleRenameClick = useLastCallback(() => {
    onRename(accountId);
  });

  const handleRemoveClick = useLastCallback(() => {
    onLogOut(accountId);
  });

  const getTriggerElement = useLastCallback(() => contentRef.current);
  const getRootElement = useLastCallback(() => document.body);
  const getMenuElement = useLastCallback(() => menuRef.current);
  const getLayout = useLastCallback((): Layout => ({
    withPortal: true,
    doNotCoverTrigger: isPortrait,
    // The shift is needed to prevent the mouse cursor from highlighting the first menu item
    topShiftY: !isPortrait ? CONTEXT_MENU_VERTICAL_SHIFT_PX : undefined,
    preferredPositionX: 'left',
    isCenteredHorizontally: true,
  }));

  const {
    isContextMenuOpen,
    isContextMenuShown,
    contextMenuAnchor,
    items,
    isBackdropRendered,
    handleBeforeContextMenu,
    handleContextMenu,
    handleContextMenuClose,
    handleContextMenuHide,
    handleMenuItemSelect,
  } = useAccountContextMenu(contentRef, {
    isPortrait,
    withContextMenu,
    onReorderClick: onReorder,
    onRenameClick: handleRenameClick,
    onRemoveClick: handleRemoveClick,
  });

  const handleKeyDown = useLastCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.code === 'Enter' || e.code === 'Space') {
      e.preventDefault();
      onClick(accountId);
    }
  });

  useLayoutEffect(() => {
    if (balanceData?.wholePart !== undefined) {
      updateFontScale();
    }
  }, [balanceData, updateFontScale, screenWidthDep]);

  const buttonClassName = buildClassName(
    styles.button,
    isActive && styles.current,
    isContextMenuOpen && OPEN_CONTEXT_MENU_CLASS_NAME,
  );

  return (
    <div className={styles.wrapper}>
      <MenuBackdrop
        isMenuOpen={isBackdropRendered}
        contentRef={contentRef}
        contentClassName={styles.contentVisible}
      />
      <div ref={contentRef} className={styles.content}>
        <div
          className={buttonClassName}
          style={getCardGradientStyle(getCardGradient(accentColorIndex))}
          aria-label={lang('Switch Account')}
          role="button"
          tabIndex={isActive ? -1 : 0}
          aria-disabled={isActive}
          onKeyDown={handleKeyDown}
          onClick={() => onClick(accountId)}
          onMouseDown={handleBeforeContextMenu}
          onContextMenu={handleContextMenu}
        >
          {balanceData && (
            <SensitiveData
              isActive={isSensitiveDataHidden}
              rows={3}
              cols={12}
              cellSize={8}
              maskClassName={styles.balanceMask}
              className={styles.balanceWrapper}
              contentClassName={styles.balanceContent}
              align="center"
            >
              <div
                ref={balanceRef}
                className={buildClassName(styles.accountBalance, 'rounded-font')}
              >
                {balanceData.currencySymbol.length === 1 && (
                  <span className={styles.currencySymbol}>{balanceData.currencySymbol}</span>
                )}
                <span>{balanceData.wholePart}</span>
                {balanceData.fractionPart && (
                  <span className={styles.fractionPart}>.{balanceData.fractionPart}</span>
                )}
                {balanceData.currencySymbol.length > 1 && (
                  <span className={styles.fractionPart}>&nbsp;{balanceData.currencySymbol}</span>
                )}
              </div>
            </SensitiveData>
          )}
          <div className={styles.accountAddressBlock}>
            {isVault && <i className="icon-lock" aria-hidden />}
            {isTestnet && <i className="icon-testnet" aria-hidden />}
            {isHardware && <i className="icon-ledger" aria-hidden />}
            {isViewMode && <i className="icon-eye-filled" aria-hidden />}
            <span>{formattedAddress}</span>
          </div>
          <i
            tabIndex={-1}
            className={buildClassName(styles.menuDots, 'icon-menu-dots')}
            aria-hidden
            onClick={handleContextMenu}
          />
        </div>
        {(title || isVault) && (
          <div className={styles.accountName}>
            {title}
            {isVault && <span className={styles.vaultBadge}>{lang('Vault')}</span>}
          </div>
        )}
        {withContextMenu && isContextMenuShown && (
          <DropdownMenu
            ref={menuRef}
            withPortal
            shouldTranslateOptions
            isOpen={isContextMenuOpen}
            items={items}
            menuAnchor={contextMenuAnchor}
            bubbleClassName={styles.menu}
            fontIconClassName={styles.menuIcon}
            getTriggerElement={getTriggerElement}
            getRootElement={getRootElement}
            getMenuElement={getMenuElement}
            getLayout={getLayout}
            onSelect={handleMenuItemSelect}
            onClose={handleContextMenuClose}
            onCloseAnimationEnd={handleContextMenuHide}
          />
        )}
      </div>
    </div>
  );
}

export default withGlobal((global, { accountId }): StateProps => ({
  accentColorIndex: selectAccountSettings(global, accountId)?.accentColorIndex,
}))(AccountWalletCard);
